// app/fullscreen.js — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { http, useTheme } from "@/app/core";


/**
 * Per-device dark/light mode toggle. Uses next-themes which persists
 * the choice to localStorage automatically.
 */
/**
 * Per-device dark/light mode toggle. Uses next-themes which persists
 * the choice to localStorage automatically. Renders an icon button with
 * a small caption underneath so dashboards can show "Light mode" /
 * "Dark mode" hints next to other toolbar controls.
 */
export function ThemeToggleButton({ className = "" }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";
  // Single icon-only button (no label below) so it lines up vertically with
  // the Waiter / Cart / Fullscreen buttons in toolbars. The aria-label + title
  // already provide accessibility context.
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={`h-10 w-10 rounded-xl ${className}`}
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="theme-toggle-button"
    >
      {mounted ? (
        isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4 opacity-0" />
      )}
    </Button>
  );
}

/**
 * Session 70.E — coalesce a burst of functional state updates into ONE
 * setState. WebSocket events arrive as separate macrotasks, so React
 * cannot batch them: a runner marking 50 orders delivered meant 50 full
 * re-renders of the staff / kitchen / bar page, which froze phones for
 * 10-20 s while the Pending badge crawled down. Updaters queue up and are
 * applied together ~60 ms after the last event (bounded at 250 ms so a
 * continuous stream still paints promptly).
 */
export function useCoalescedUpdater(setState) {
  const updatersRef = useRef([]);
  const timerRef = useRef(null);
  const deadlineRef = useRef(0);
  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    deadlineRef.current = 0;
    const updaters = updatersRef.current;
    if (!updaters.length) return;
    updatersRef.current = [];
    setState((prev) => {
      let next = prev;
      for (const fn of updaters) {
        try { next = typeof fn === "function" ? fn(next) : fn; } catch (_) { /* one bad patch must not drop the rest */ }
      }
      return next;
    });
  }, [setState]);
  const queue = useCallback((updater) => {
    updatersRef.current.push(updater);
    const now = Date.now();
    if (!deadlineRef.current) deadlineRef.current = now + 150;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, Math.max(0, Math.min(30, deadlineRef.current - now)));
  }, [flush]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return queue;
}

/**
 * Browser fullscreen toggle. Uses the standard Fullscreen API and listens to
 * fullscreenchange so the icon/label stay in sync if the user presses Esc.
 * Renders the same labelled icon-button shape as ThemeToggleButton for a
 * consistent toolbar look.
 */
export function FullscreenToggleButton({ className = "", lockDown = false }) {
  const [supported, setSupported] = useState(true);
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const supports =
      document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled;
    setSupported(Boolean(supports));

    const sync = () => {
      const el =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFs(Boolean(el));
    };
    sync();
    const events = ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"];
    events.forEach((e) => document.addEventListener(e, sync));
    return () => events.forEach((e) => document.removeEventListener(e, sync));
  }, []);

  // Lockdown layer — when a staff/kitchen/bar dashboard renders this with
  // lockDown=true AND we're currently in fullscreen, we:
  //   1) Attempt navigator.keyboard.lock(['Escape']) so pressing Esc
  //      doesn't drop fullscreen on Chromium-based desktop browsers.
  //   2) Block the contextmenu event so right-click / long-press doesn't
  //      open the browser exit-fullscreen prompt.
  // Honest limitations: Safari/Firefox don't expose Keyboard Lock. Mobile
  // browsers expose a native exit-fullscreen UI that JS cannot remove.
  // Holding Esc long-press on Chromium will still bypass keyboard lock —
  // that's an intentional browser security behaviour. The audit log
  // captures any sign-out regardless, so owners can spot patterns.
  useEffect(() => {
    if (!lockDown || !isFs || typeof document === "undefined") return undefined;
    let unlock = null;
    try {
      if (navigator?.keyboard?.lock) {
        const p = navigator.keyboard.lock(["Escape"]);
        if (p && typeof p.then === "function") p.catch(() => {});
        unlock = () => {
          try { navigator.keyboard.unlock(); } catch (_) { /* noop */ }
        };
      }
    } catch (_) {
      /* keyboard lock not available — soldier on */
    }
    const blockMenu = (event) => { event.preventDefault(); };
    document.addEventListener("contextmenu", blockMenu, true);
    return () => {
      document.removeEventListener("contextmenu", blockMenu, true);
      if (unlock) unlock();
    };
  }, [lockDown, isFs]);

  const toggle = useCallback(async () => {
    try {
      if (typeof document === "undefined") return;
      const el = document.documentElement;
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      if (!fsEl) {
        const req =
          el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.mozRequestFullScreen ||
          el.msRequestFullscreen;
        if (req) await req.call(el);
        setFullscreenLock(true);           // Session 70.G — operator wants it locked on
      } else {
        setFullscreenLock(false);          // Session 70.G — operator turned it off themselves
        fsIntentionalExit = true;
        const exit =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.mozCancelFullScreen ||
          document.msExitFullscreen;
        if (exit) await exit.call(document);
      }
    } catch (err) {
      toast.error(
        err && err.message
          ? `Fullscreen unavailable: ${err.message}`
          : "Fullscreen is not available right now."
      );
    }
  }, []);

  if (!supported) return null;
  // For lockdown dashboards (staff/manager/supervisor/waiter/kitchen/bar),
  // hide the toggle once in fullscreen so the only exit is via Logout
  // (which calls exitFullscreenIfActive first). For Owner / Admin views
  // (lockDown=false) the toggle stays visible and switches to Minimize so
  // they can drop out of fullscreen freely.
  if (lockDown && isFs) return null;

  const label = isFs ? "Exit full screen" : "Full screen";

  // Icon-only button — keeps the same h-10 footprint as ThemeToggleButton
  // so they line up cleanly in toolbar rows on mobile. The aria-label +
  // title carry the meaning for keyboard / screen reader users.
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className={`h-10 w-10 rounded-xl ${className}`}
      onClick={toggle}
      aria-label={label}
      title={label}
      data-testid="fullscreen-toggle-button"
    >
      {isFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
    </Button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Session 70.G — FULLSCREEN LOCK. The browser drops fullscreen on its own
// for things the operator never asked for: a native confirm() dialog, a
// receipt print, the service-worker update reload, some Android keyboard
// pop-ups. Staff reported "it keeps jumping out of full screen". When the
// operator turned fullscreen ON we remember that (per tab, survives a
// reload) and, whenever the browser leaves fullscreen without an explicit
// toggle/logout, we re-enter it on the very next tap/keypress — the only
// moment the Fullscreen API lets a page do that.
const FS_LOCK_KEY = "nfcs:fullscreen-lock";
let fsIntentionalExit = false;
let fsRearmed = false;

function _fsElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
}
function fullscreenLockWanted() {
  try { return window.sessionStorage.getItem(FS_LOCK_KEY) === "1"; } catch (_) { return false; }
}
function setFullscreenLock(on) {
  try { if (on) window.sessionStorage.setItem(FS_LOCK_KEY, "1"); else window.sessionStorage.removeItem(FS_LOCK_KEY); } catch (_) { /* noop */ }
}
async function requestFullscreenNow() {
  const el = document.documentElement;
  const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
  if (req) await req.call(el);
}
function armFullscreenReentry() {
  if (fsRearmed || typeof document === "undefined") return;
  fsRearmed = true;
  const events = ["pointerup", "touchend", "keydown"];
  const handler = async () => {
    if (!fullscreenLockWanted() || _fsElement()) { cleanup(); return; }
    try { await requestFullscreenNow(); } catch (_) { /* needs another gesture — stay armed */ return; }
    cleanup();
  };
  const cleanup = () => {
    fsRearmed = false;
    events.forEach((e) => document.removeEventListener(e, handler, true));
  };
  events.forEach((e) => document.addEventListener(e, handler, true));
  try { toast.message("Tap anywhere to return to full screen.", { id: "fs-reenter", duration: 4000 }); } catch (_) { /* noop */ }
}
if (typeof document !== "undefined" && typeof window !== "undefined" && !window.__nfcsFsKeeper) {
  window.__nfcsFsKeeper = true;
  const onChange = () => {
    if (_fsElement()) { fsRearmed = false; return; }   // entered → nothing to do
    if (fsIntentionalExit) { fsIntentionalExit = false; return; }
    if (fullscreenLockWanted()) armFullscreenReentry();
  };
  ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"].forEach((e) => document.addEventListener(e, onChange));
  // After a reload (service-worker update, hard refresh) we come back
  // windowed although the lock is still set → re-enter on first tap.
  window.addEventListener("load", () => { if (fullscreenLockWanted() && !_fsElement()) armFullscreenReentry(); });
  if (document.readyState === "complete" && fullscreenLockWanted() && !_fsElement()) armFullscreenReentry();
}

// Best-effort fullscreen exit used by logout handlers. Quiet no-op when
// not in fullscreen or when the browser doesn't expose the API. Lives at
// module scope so all handleLogout implementations can call it.
export async function exitFullscreenIfActive() {
  try {
    if (typeof document === "undefined") return;
    setFullscreenLock(false);          // Session 70.G — logout releases the lock
    fsIntentionalExit = true;
    // Release any keyboard lock first so the browser isn't left in a
    // weird state where Esc is captured but we're no longer in fullscreen.
    try {
      if (navigator?.keyboard?.unlock) navigator.keyboard.unlock();
    } catch (_) {
      /* noop */
    }
    const fsEl =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;
    if (!fsEl) return;
    const exit =
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    if (exit) await exit.call(document);
  } catch (_err) {
    // Swallow — we never want to block a logout because of a fullscreen
    // quirk on some browser/OS combo.
  }
}

// ─────────────────────────────────────────────────────────────────────
// Session 27.62 — Logout suppression window.
//
// When the operator taps Log out, we clear session tokens and navigate
// away. But there's a race: any in-flight authenticated request that
// was already dispatched will come back with a 401 "Invalid session"
// AFTER the session has been cleared, triggering a cosmetic
// `toast.error("Invalid session")` on the login screen. The user
// reported this as: "shows an Invalid Session toast the second I hit
// Log out — everything worked fine, it just looks scary."
//
// The fix: expose a `beginLogoutSuppression()` helper that every
// handleLogout implementation calls FIRST. It sets a global flag for
// 3 seconds. During that window an axios response interceptor absorbs
// any 401 response by returning a never-resolving promise — so the
// downstream `.catch` never fires, no toast surfaces, and the browser
// simply drops the doomed request when the tab reloads/routes away.
//
// Trade-off: the 2-3 in-flight requests during logout leak their
// promise into GC-limbo. That's fine — the tab is being torn down
// anyway; on the next login a fresh axios instance is effectively
// used (fresh session tokens on every call).
if (typeof window !== "undefined") {
  window.__nfcsLoggingOutUntil = window.__nfcsLoggingOutUntil || 0;
}
export function beginLogoutSuppression(windowMs = 3000) {
  try {
    if (typeof window !== "undefined") {
      window.__nfcsLoggingOutUntil = Date.now() + Math.max(500, windowMs);
    }
  } catch (_) { /* noop */ }
}
function _isLogoutSuppressionActive() {
  try {
    if (typeof window === "undefined") return false;
    return Number(window.__nfcsLoggingOutUntil || 0) > Date.now();
  } catch (_) { return false; }
}
// Install the interceptor on the shared `http` instance. Order matters:
// this must come AFTER the existing retry interceptor so retried
// requests are also suppressed if they finish inside the window.
http.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      if (status === 401 && _isLogoutSuppressionActive()) {
        // Return a never-resolving promise so downstream .catch()
        // handlers (which typically toast the detail) never fire.
        return new Promise(() => { /* intentionally never resolves */ });
      }
    } catch (_) { /* fall through */ }
    return Promise.reject(error);
  }
);
